/**
 * useSuggestions.js  —  v11.2  (pure frontend, zero network calls)
 *
 * WHY NO BACKEND CALL
 * -------------------
 * The previous approach (GET /api/suggestions) hit a live BI model for field
 * values, causing 9+ timeout warnings per keystroke (PBI latency ~2-3s).
 * All the data we need is already loaded in memory by useWorkspaceBootstrap:
 *   • synonyms         — measure/dimension/value aliases
 *   • metadata         — all model columns and measure names
 *   • businessSemantic — KPI display names and synonyms
 *
 * Result: suggestions appear in < 1ms instead of 250–1500ms.
 *
 * USAGE
 * -----
 * Pass the already-loaded synonyms, metadata, businessSemantic so the engine
 * can be (re)built only when the data changes, not on every keystroke.
 *
 *   const { suggestions, ... } = useSuggestions(message, synonyms, metadata, businessSemantic)
 *
 * The engine is memoized via useMemo — it rebuilds only when synonyms/metadata
 * or businessSemantic reference changes (i.e. after bootstrap or a save).
 */

import { useMemo, useState, useCallback } from 'react'
import { buildSuggestionEngine } from '../lib/suggestionEngine'
import { suggestionToken } from '../lib/queryWorkspaceUtils'

export function useSuggestions(message, synonyms, metadata, businessSemantic) {
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0)
  const [dismissed, setDismissed] = useState(false)

  // Build the engine once per data load — memoized, no re-build on keystrokes
  const suggest = useMemo(
    () => buildSuggestionEngine(synonyms, metadata, businessSemantic),
    [synonyms, metadata, businessSemantic]
  )

  // Derive suggestions synchronously on every message change — no async, no timer
  const token = suggestionToken(message)
  const rawSuggestions = useMemo(() => {
    if (!token || token.length < 2) return []
    return suggest(token, 8)
  }, [suggest, token])

  // Reset dismissed state when the token changes (user typed something new)
  const [prevToken, setPrevToken] = useState(token)
  if (token !== prevToken) {
    setPrevToken(token)
    if (dismissed) setDismissed(false)
  }

  const suggestions = dismissed ? [] : rawSuggestions

  // Reset active index whenever suggestions list changes
  if (activeSuggestionIndex >= suggestions.length && activeSuggestionIndex !== 0) {
    setActiveSuggestionIndex(0)
  }

  const clearSuggestions = useCallback(() => {
    setActiveSuggestionIndex(0)
    setDismissed(true)
  }, [])

  return {
    suggestions,
    activeSuggestionIndex,
    setActiveSuggestionIndex,
    clearSuggestions,
  }
}
